import Actions from '@mm/state/actions/actions';
import AdAccountCRUDActions from '@mm/state/actions/AdAccountCRUDActions';
import AdConfigCRUDActions from '@mm/state/actions/AdConfigCRUDActions';
import audiencesCRUDActions from '@mm/state/actions/AudiencesCRUDActions';
import PageCRUDActions from '@mm/state/actions/PageCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import AdConfigController from '@mm/state/controllers/AdConfigController';
import AdSetController from '@mm/state/controllers/AdSetController';
import {
  AdSetBudgetType,
  CallToAction,
  CampaignBudgetType,
  SpecialAdCategory,
} from '@mm/state/interfaces/adAccounts';
import AdAccountModel from '@mm/state/model/AdAccountModel';
import AdConfigModel, {
  ExtendedAdConfigModel,
  MMTargeting,
  OptimizationGoal,
} from '@mm/state/model/AdConfigModel';
import { AdModel } from '@mm/state/model/AdModel';
import { AdSetWithRelationsModel } from '@mm/state/model/AdSetModel';
import PageModel from '@mm/state/model/PageModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  activeChatbotIdSelector,
  activeChatbotSelector,
} from '@mm/state/selectors/viewSelectors';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import {
  assign,
  isArray,
  isEmpty,
  isEqual,
  noop,
  omit,
  uniq,
  uniqBy,
} from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import { addNamesToAccounts } from '../../../../../common/helpers/utils';
import { useFormSteps } from '../../../../../common/hooks/use-form-steps';
import { usePrevious } from '../../../../../common/hooks/use-previous';
import { DialogueSettingsButton } from '../../../../common/DialogueSettingsButton/DialogueSettingsButton';
import { DEFAULT_CURRENCY, symbolForCurrency } from '../../../../common/utils';
import { InactiveBotAlert } from '../../../../components/InactiveBotAlert/InactiveBotAlert';
import { DialogueHeaderWarningAlert } from '../../../common/components/DialogueHeaderWarningAlert/DialogueHeaderWarningAlert';
import { StartBotAdsSelector } from '../../../startBotPromoters/common/StartBotSelector/StartBotSelector';
import {
  BUDGET_THRESHOLD,
  DATE_FORMAT,
  DEFAULT_AD_SET_BUDGET,
  DEFAULT_CAMPAIGN_BUDGET,
} from '../../constants/constants';
import { cleanupFetcher, isValidStep, statefulPoller } from '../../utils/utils';
import '../AdDetail.pcss';
import { November2020EuroAdsChangesAlert } from '../November2020EuroAdsChangesAlert/November2020EuroAdsChangesAlert';
import { AdAccountStep } from './AdAccountStep/AdAccountStep';
import { AdCampaignStep } from './AdCampaignStep/AdCampaignStep';
import './AdConfigDetail.pcss';
import { AdCreativeStep } from './AdCreativeStep/AdCreativeStep';
import { AdSetStep } from './AdSetStep/AdSetStep';
import { EditAdConfigHeader } from './EditAdConfigHeader/EditAdConfigHeader';
import { formStepContainerBuilder } from './FormStepContainer/FormStepContainer';
import { StartBotStep } from './StartBotStep/StartBotStep';
import {
  defaultTargeting,
  prepareNormalTargeting,
  prepareSpecialTargeting,
} from './Targeting/utils';
import { FormValues, NumericBudgeting, RouteParams } from './types';
import {
  adCloning,
  adSetCloning,
  offsetForAccount,
  prepareInitialFormValues,
  processUrl,
  resolveMismatchingAudiences,
} from './utils';
import { prepareValidations } from './validations';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import { EmbeddableDialogueEditorWithPreview } from '../../../startBotPromoters/common/StartBotsDialogueEditor/EmbeddableDialogueEditorWithPreview';
import classNames from 'classnames';

const noPastDate = (date: Date) => moment.max(moment(date), moment());

const stepFields = [
  ['adAccount'],
  ['campaignName', 'campaignBudget', 'campaignBudgetType', 'specialAdCategory'],
  [
    'adSetName',
    'sourceAdSet',
    'adSetBudgetType',
    'adSetBudget',
    'adSetStartDate',
    'adSetEndDate',
    'bidAmount',
    'targeting',
  ],
  [
    'sourceAd',
    'creativeText',
    'creativeHeadline',
    'creativeDescription',
    'assetId',
    'thumbId',
  ],
];

const campaignOptimizationOn = (adConfig: AdConfigModel | null) =>
  adConfig ? adConfig.campaignBudget !== null : false;
const adSetOptimizationOn = (adConfig: AdConfigModel | null) =>
  adConfig ? adConfig.adSetBudget !== null : true;

const useCampaignBudgetOptimization = (adConfig: AdConfigModel | null) => {
  const [budgetOptimizationOn, setBudgetOptimizationOn] = useState(false);
  const [valueSet, setValueSet] = useState(false);
  useEffect(() => {
    if (adConfig && !valueSet) {
      setValueSet(true);
      setBudgetOptimizationOn(campaignOptimizationOn(adConfig));
    }
  }, [adConfig]);
  return { budgetOptimizationOn, setBudgetOptimizationOn };
};

const useAdSetBudgetOptimization = (adConfig: AdConfigModel | null) => {
  const [adSetBudgetSelected, setAdSetBudgetSelected] = useState(true);
  const [valueSet, setValueSet] = useState(false);
  useEffect(() => {
    if (adConfig && !valueSet) {
      setValueSet(true);
      setAdSetBudgetSelected(adSetOptimizationOn(adConfig));
    }
  }, [adConfig]);
  return { adSetBudgetSelected, setAdSetBudgetSelected };
};

const accountCurrency = (formValues: FormValues) =>
  formValues.adAccount?.currency || DEFAULT_CURRENCY;
const isScheduledContinuously = (
  adConfig: AdConfigModel | null,
  formValues: FormValues,
) => {
  return (
    !adConfig?.adSetEndDate &&
    formValues.adSetBudgetType !== AdSetBudgetType.LIFETIME
  );
};

const adSetsPoller = statefulPoller<AdSetWithRelationsModel>();
const adsPoller = statefulPoller<AdModel>();

type Props = {
  adConfigEntity?: AdConfigModel;
  active?: boolean;
  indexOffset?: number;
  children?: (params: {
    startOver: () => void;
    currentStep: number;
    currentAd: AdConfigModel | null;
  }) => JSX.Element | JSX.Element[] | null;
  usedInSlideout?: boolean;
  onActivateSuccess?: () => void;
};

export const AdConfigDetail = (props: Props) => {
  const {
    adConfigEntity,
    children,
    active = true,
    indexOffset = 0,
    usedInSlideout,
    onActivateSuccess,
  } = props;
  const {
    match: {
      params: { chatbotId, adConfigId },
    },
  } = useRouter<RouteParams>();
  const [accountId, setAccountId] = useState('');
  const allAdSets = useSelector((state: StoreState) => state.adEntities.adSets);
  const adSets = allAdSets[accountId]?.data || [];
  const showAdSetsSpinner = Boolean(allAdSets[accountId]?.next);
  const allAds = useSelector((state: StoreState) => state.adEntities.ads);
  const ads = allAds[accountId]?.data || [];
  const showAdsSpinner = Boolean(allAds[accountId]?.next);
  const botId = useSelector(activeChatbotIdSelector);
  const [page, setPage] = useState<PageModel | null>(null);
  const [adConfig, setAdConfig] = useState<AdConfigModel | null>(null);
  const [localUpdates, setLocalUpdates] = useState({});
  const [adAccounts, facebookPage, session] = useSelector(
    (state: StoreState) => {
      return [
        state.adAccounts.entities,
        activeFacebookPageSelector(state),
        state.session.session,
      ] as const;
    },
    shallowEqual,
  );

  const activeBot = useSelector(activeChatbotSelector);
  const pageWidgets = useSelector((state: StoreState) =>
    widgetsByDialogueSelector(
      page ? page.id : undefined,
      state.widget.entities,
    ),
  );
  const prevPageWidgets = usePrevious({ widgets: pageWidgets });
  const [updated, setUpdated] = useState(false);
  const [isVideoMedia, setIsVideoMedia] = useState(true);
  const [touchedForms, setTouchedForms] = useState(0);
  const [nextClicked, setNextClicked] = useState(-1);
  const [fullyInitialized, setFullyInitialized] = useState(false);
  const [remoteValidations, setRemoteValidations] = useState<{
    [key in keyof FormValues]?: string[];
  }>({});
  const accountsWithNamesFetcher = useCallback(
    () => uniqBy(addNamesToAccounts(adAccounts as AdAccountModel[]), 'id'),
    [adAccounts],
  );
  const accounts = accountsWithNamesFetcher();
  const [formValues, setFormValues] = useState<FormValues>(
    prepareInitialFormValues(null, adSets, accounts, ads),
  );
  const connected =
    Boolean(adConfig?.adAccountId) &&
    (Boolean(adConfig?.userConnected) || formValues.adAccount !== null);
  const widgetCRUDActions = useWidgetCRUDActions();
  const dispatch: Dispatch<StoreState> = useDispatch();
  useEffect(() => {
    (async () => {
      let ad: AdConfigModel;
      if (adConfigEntity) {
        ad = adConfigEntity;
      } else {
        ad = await dispatch(
          AdConfigCRUDActions.retrieveById(botId, Number(adConfigId)),
        );
        if (ad.draft) {
          dispatch(audiencesCRUDActions.retrieveAll(botId));
        }
      }
      if (!ad.draft) {
        if (!ad.publishingProcess) {
          ad = await dispatch(
            AdConfigCRUDActions.prepareReplacementPage(botId, Number(ad.id)),
          );
        }
        setTouchedForms(5);
        setNextClicked(4);
      } else if (ad.progressStep !== null) {
        setTouchedForms(ad.progressStep === 4 ? 5 : ad.progressStep);
        setNextClicked(ad.progressStep);
      } else {
        setNextClicked(0);
      }
      await prepareAdsAndAdSets(ad.adAccountId);
      setAdConfig(ad);
    })();
    return () => {
      adsPoller(null, cleanupFetcher, noop);
      adSetsPoller(null, cleanupFetcher, noop);
    };
  }, []);
  useEffect(() => {
    if (adConfig && adConfig.sourceAdSetId && !formValues?.sourceAdSet?.id) {
      const foundAdSet = adSets.find(
        adSet => adSet.id === adConfig.sourceAdSetId,
      );
      if (foundAdSet) {
        setFormValues(prevFormValues => ({
          ...prevFormValues,
          sourceAdSet: foundAdSet,
        }));
      }
    }
  }, [adSets]);
  useEffect(() => {
    if (!adConfig) {
      return;
    }
    (async () => {
      const pageId = adConfig.replacementPageId || adConfig.pageId;
      widgetCRUDActions.retrieveAll(pageId);
      const newPage = await dispatch(PageCRUDActions.retrieveById(pageId));
      setPage(newPage);
    })();
  }, [adConfig?.pageId, adConfig?.replacementPageId]);

  useEffect(() => {
    if (adConfig && adConfig.sourceAdId && !formValues?.sourceAd?.id) {
      const foundAd = ads.find(ad => ad.id === adConfig.sourceAdId);
      if (foundAd) {
        setIsVideoMedia(Boolean(foundAd.videoUrl));
        setFormValues(prevFormValues => ({
          ...prevFormValues,
          sourceAd: foundAd,
          imageUrl: processUrl(
            adConfig.imageUrl,
            Boolean(adConfig.assetId && !adConfig.videoUrl),
            foundAd.imageUrl,
          ),
          videoUrl: processUrl(
            adConfig.videoUrl,
            Boolean(adConfig.assetId && !adConfig.imageUrl),
            foundAd.videoUrl,
          ),
          thumbUrl: processUrl(
            adConfig.thumbUrl,
            Boolean(adConfig.thumbId),
            foundAd.thumbUrl,
          ),
        }));
      }
    }
  }, [ads]);
  useEffect(() => {
    if (adConfig && adAccounts?.length > 0 && !fullyInitialized) {
      const values = prepareInitialFormValues(adConfig, adSets, accounts, ads);
      setAccountId(values.adAccount?.id || '');
      setFormValues(values);
      setIsVideoMedia(Boolean(values.videoUrl && !adConfig.imageUrl));
      setFullyInitialized(true);
    }
  }, [adAccounts, adConfig]);
  useEffect(() => {
    const prevWidgets = (prevPageWidgets || { widgets: [] }).widgets;
    if (!isEqual(pageWidgets, prevWidgets) && prevWidgets.length > 0) {
      setUpdated(true);
      setTouchedForms(5);
    }
  }, [pageWidgets]);
  const testBot = () => {
    if (facebookPage && page) {
      window.open(
        `http://m.me/${facebookPage.remoteId}?ref=${page.searchToken}_${session.searchToken}`,
      );
    }
  };
  const cloneAdSetSelected = adSetCloning(adConfig);
  const cloneAdSelected = adCloning(adConfig);

  const prepareCloneAdFields = (adCandidate?: AdModel) => {
    const commonFields = {
      creativeText: adCandidate?.creativeText || '',
      creativeDescription: adCandidate?.creativeDescription || '',
      creativeHeadline: adCandidate?.creativeHeadline || '',
      assetId: null,
      thumbId: null,
    };
    return [
      {
        ...commonFields,
        sourceAdId: adCandidate?.id || null,
      },
      {
        ...commonFields,
        sourceAd: adCandidate || null,
        videoAssetId: null,
        imageAssetId: null,
        thumbAssetId: null,
        videoUrl: adCandidate?.videoUrl,
        imageUrl: adCandidate?.imageUrl,
        thumbUrl: adCandidate?.thumbUrl,
      },
    ];
  };

  const onSelectCloneAd = (id?: string) => {
    const adCandidate =
      id !== undefined
        ? ads.find(ad => ad.id === id)
        : formValues.sourceAd || ads[0];
    if (id !== undefined && !adCandidate) {
      return;
    }
    if (adCandidate) {
      setIsVideoMedia(Boolean(adCandidate.videoUrl));
    }
    const [changes, formChanges] = prepareCloneAdFields(adCandidate);
    handleRemoteUpdate(changes, formChanges, 'sourceAd');
  };

  const creativeField = (
    fieldName: 'creativeText' | 'creativeDescription' | 'creativeHeadline',
  ) => {
    const sourceAdField = formValues.sourceAd?.[fieldName] || '';
    const formField = formValues[fieldName] || '';
    if (!formField) {
      return sourceAdField;
    }
    return formField === sourceAdField ? '' : formField;
  };

  const onSelectNewAd = () => {
    const commonFields = {
      creativeText: creativeField('creativeText'),
      creativeDescription: creativeField('creativeDescription'),
      creativeHeadline: creativeField('creativeHeadline'),
    };
    handleRemoteUpdate(
      {
        ...commonFields,
        sourceAdId: null,
      },
      {
        ...commonFields,
        videoUrl: formValues.videoAssetId ? formValues.videoUrl : null,
        thumbUrl: formValues.thumbAssetId ? formValues.thumbUrl : null,
        imageUrl: formValues.imageAssetId ? formValues.imageUrl : null,
      },
    );
  };

  const onSelectCloneAdSet = () => {
    const adSetCandidate = formValues.sourceAdSet || adSets[0];
    handleRemoteUpdate(
      {
        targeting: null,
        ...(adSetCandidate ? { sourceAdSetId: adSetCandidate.id } : {}),
      },
      { sourceAdSet: adSetCandidate || null },
    );
  };

  const onSelectNewAdSet = () => {
    handleRemoteUpdate({
      targeting: formValues.targeting || defaultTargeting(),
      sourceAdSetId: null,
    });
  };

  const { budgetOptimizationOn, setBudgetOptimizationOn } =
    useCampaignBudgetOptimization(adConfig);
  const { adSetBudgetSelected, setAdSetBudgetSelected } =
    useAdSetBudgetOptimization(adConfig);
  const specialAdCategoryChecked = Boolean(adConfig?.specialAdCategory);
  const scheduledContinuously = isScheduledContinuously(adConfig, formValues);
  const customAudiencesMismatching = resolveMismatchingAudiences(
    adAccounts.find(
      (account: AdAccountModel) => account.id === formValues.adAccount?.id,
    ),
    formValues.sourceAdSet,
    cloneAdSetSelected,
  );
  const validations = prepareValidations(
    adConfig,
    formValues,
    remoteValidations,
    customAudiencesMismatching,
    budgetOptimizationOn,
    isVideoMedia,
  );
  const valid = isEmpty(validations) && connected;
  const draft = adConfig?.draft;
  const currency = symbolForCurrency(accountCurrency(formValues));

  const prepareAdsAndAdSets = async (accountId: string) => {
    let adsResolved: (data: AdModel[]) => void = noop;
    let adSetsResolved: (data: AdSetWithRelationsModel[]) => void = noop;
    const adsPromise = new Promise<AdModel[]>(resolve => {
      adsResolved = resolve;
      return;
    });
    const adSetsPromise = new Promise<AdSetWithRelationsModel[]>(resolve => {
      adSetsResolved = resolve;
      return;
    });
    const adSetsFetcher = async (next?: string | null) => {
      const results = await AdSetController.allNonDeprecated({
        accountId,
        limit: 30,
        botId,
        next: next || allAdSets[accountId]?.next || undefined,
      });
      adSetsResolved(results.data);
      return results;
    };
    const adsFetcher = async (next?: string | null) => {
      const results = await AdConfigController.availableAds({
        accountId,
        limit: 30,
        botId,
        next: next || allAds[accountId]?.next || undefined,
      });
      adsResolved(results.data);
      return results;
    };
    const onAdsFetched = (data: AdModel[], next: string | null) =>
      dispatch({
        type: Actions.RETRIEVED_ADS,
        payload: { id: accountId, data, next },
      });
    const onAdSetsFetched = (
      data: AdSetWithRelationsModel[],
      next: string | null,
    ) =>
      dispatch({
        type: Actions.RETRIEVED_AD_SETS,
        payload: { id: accountId, data, next },
      });
    dispatch({ type: Actions.AD_ENTITIES_BUSY });
    if (allAdSets[accountId]?.data !== undefined) {
      adSetsResolved(allAdSets[accountId].data);
    }
    if (allAds[accountId]?.data !== undefined) {
      adsResolved(allAds[accountId].data);
    }
    if (allAds[accountId]?.data === undefined || allAds[accountId]?.next) {
      adsPoller(accountId, adsFetcher, onAdsFetched);
    }
    if (
      allAdSets[accountId]?.data === undefined ||
      allAdSets[accountId]?.next
    ) {
      adSetsPoller(accountId, adSetsFetcher, onAdSetsFetched);
    }
    try {
      await dispatch(AdAccountCRUDActions.retrieve(accountId, true));
    } catch {
      dispatch(AdAccountCRUDActions.unbusy());
    }
    Promise.all([adsPromise, adSetsPromise])
      .then(() => {
        dispatch({ type: Actions.AD_ENTITIES_UNBUSY });
        return [null, null];
      })
      .catch(() => {
        dispatch({ type: Actions.AD_ENTITIES_UNBUSY });
      });
    return { adsPromise, adSetsPromise };
  };

  const budgetValue = (key: keyof NumericBudgeting) => {
    const amount = formValues[key];
    const changes: Partial<AdConfigModel> = {};
    if (amount === undefined || amount === null || amount === '') {
      changes[key] = null;
    } else {
      const numericAmount = Number(amount);
      if (!isNaN(numericAmount) && numericAmount < BUDGET_THRESHOLD) {
        changes[key] = numericAmount * offsetForAccount(formValues.adAccount);
      }
    }
    return changes;
  };

  const adSetBudgetingValues = (budgetSelected: boolean) => {
    const budgetType = formValues.adSetBudgetType || AdSetBudgetType.DAILY;
    const budget = Number(formValues.adSetBudget) || DEFAULT_AD_SET_BUDGET;
    const budgetOnValues: {
      adSetBudgetType: AdSetBudgetType | null;
      adSetBudget: number | null;
    } = budgetSelected
      ? {
          adSetBudgetType: null,
          adSetBudget: null,
        }
      : {
          adSetBudgetType: budgetType,
          adSetBudget: budget,
        };
    const changes = budgetSelected
      ? budgetOnValues
      : {
          ...budgetOnValues,
          adSetBudget: budget * offsetForAccount(formValues.adAccount),
        };
    return [budgetOnValues, changes];
  };

  const onBudgetOptimizationToggle = () => {
    const budgetType =
      formValues.campaignBudgetType || CampaignBudgetType.DAILY;
    const adSetBudget = Number(formValues.adSetBudget) || 0;
    const budget =
      Number(formValues.campaignBudget) ||
      Math.max(adSetBudget * 2, DEFAULT_CAMPAIGN_BUDGET);
    const budgetOnValues = budgetOptimizationOn
      ? {
          campaignBudgetType: null,
          campaignBudget: null,
        }
      : {
          adSetBudgetType: AdSetBudgetType.DAILY,
          campaignBudgetType: budgetType,
          campaignBudget: budget,
        };
    const [adSetBudgetOnValues, adSetChanges] = adSetBudgetingValues(false);
    const changes = budgetOptimizationOn
      ? { campaignBudgetType: null, campaignBudget: null, ...adSetChanges }
      : {
          ...budgetOnValues,
          adSetBudgetType: null,
          adSetBudget: null,
          campaignBudget: budget * offsetForAccount(formValues.adAccount),
        };

    handleRemoteUpdate(changes, budgetOnValues, 'campaignBudget');
    setBudgetOptimizationOn(!budgetOptimizationOn);
  };

  const handleAccountChange = async (id: string) => {
    if (!adConfig || !adConfig.draft) {
      return;
    }
    const account = adAccounts.find((a: AdAccountModel) => a.id === id);
    const accountWithName = accounts.find(a => a.id === id);
    if (!account || !accountWithName) {
      return;
    }
    setAccountId(id);
    const { adsPromise, adSetsPromise } = await prepareAdsAndAdSets(account.id);
    const ads = await adsPromise;
    const adSets = await adSetsPromise;
    const changes: Partial<{
      [P in keyof ExtendedAdConfigModel]: ExtendedAdConfigModel[P];
    }> = {
      adAccountId: account.id.toString(),
      adAccountName: account.name,
      differentUser: account.differentUser,
    };
    const formChanges: Partial<{ [P in keyof FormValues]: FormValues[P] }> = {
      adAccount: accountWithName,
    };
    if (cloneAdSetSelected) {
      changes['sourceAdSetId'] = adSets[0]?.id || null;
      formChanges['sourceAdSet'] = adSets[0] || null;
    }
    if (cloneAdSelected) {
      const adCandidate = ads[0];
      const [creativeChanges, creativeFormChanges] =
        prepareCloneAdFields(adCandidate);
      if (adCandidate) {
        setIsVideoMedia(Boolean(adCandidate.videoUrl));
      }
      assign(changes, creativeChanges);
      assign(formChanges, creativeFormChanges);
    }
    handleRemoteUpdate(changes, formChanges, 'campaignName');
  };

  const onAdSetChange = (adSetId: number) => {
    handleSetChange(adSetId.toString());
  };

  const onAdChange = (adId: number) => {
    const id = adId.toString();
    onSelectCloneAd(id);
  };

  const handleSetChange = (id: string) => {
    if (!adConfig || !adConfig.draft) {
      return;
    }
    const adSet = adSets.find(a => a.id === id);
    adSet &&
      handleRemoteUpdate(
        {
          sourceAdSetId: adSet.id,
        },
        {
          sourceAdSet: adSet,
        },
        'sourceAdSet',
      );
  };

  const onSelectVideoMedia = () => {
    setIsVideoMedia(true);
    const value = formValues.videoAssetId || null;
    handleRemoteUpdate({
      assetId: value,
    });
  };

  const onSelectImageMedia = () => {
    setIsVideoMedia(false);
    const value = formValues.imageAssetId || null;
    handleRemoteUpdate({
      assetId: value,
    });
  };

  const handleNameSave = (name: string) => {
    handleRemoteUpdate({ name }, {}, 'name');
  };

  const addRemoteValidation = (text: string[], field?: keyof FormValues) => {
    if (!field) {
      return;
    }
    setRemoteValidations({
      ...remoteValidations,
      [field]: uniq([...(remoteValidations[field] || []), ...text]),
    });
  };

  const removeRemoteValidation = (field?: keyof FormValues) => {
    if (!field) {
      return;
    }
    setRemoteValidations(omit(remoteValidations, field));
  };

  const handleChangeWrapper =
    (name: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormValues({ ...formValues, [name]: value });
    };
  const handleUpdatedTextWrapper =
    (name: keyof FormValues) => (value: string) => {
      setFormValues({ ...formValues, [name]: value });
    };

  const updateRemoteEntity = async (
    updates: Partial<AdConfigModel>,
    mapValidationField?: keyof FormValues,
  ) => {
    const newEntityOrError = await dispatch(
      AdConfigCRUDActions.update(botId, updates),
    );
    if (isArray(newEntityOrError)) {
      addRemoteValidation(newEntityOrError, mapValidationField);
    } else {
      removeRemoteValidation(mapValidationField);
      setAdConfig(newEntityOrError);
    }
  };

  const handleRemoteUpdate = async (
    changes: Partial<{
      [P in keyof ExtendedAdConfigModel]: ExtendedAdConfigModel[P];
    }>,
    formChanges?: Partial<{ [P in keyof FormValues]: FormValues[P] }>,
    mapValidationField?: keyof FormValues,
  ) => {
    if (!isEmpty(changes) && adConfig) {
      const updates = { id: adConfig.id, ...changes };
      if (draft) {
        await updateRemoteEntity(updates, mapValidationField);
      } else {
        setLocalUpdates(prevUpdates => ({ ...prevUpdates, ...updates }));
      }
    }
    if (!isEmpty(formChanges)) {
      setFormValues({ ...formValues, ...formChanges });
    }
  };

  const onScheduleRanged = () => {
    handleRemoteUpdate({
      adSetStartDate: formValues.adSetStartDate.format(DATE_FORMAT),
      adSetEndDate: formValues.adSetEndDate.format(DATE_FORMAT),
    });
  };

  const onScheduleContinuously = () => {
    handleRemoteUpdate({
      adSetStartDate: formValues.adSetStartDate.format(DATE_FORMAT),
      adSetEndDate: null,
    });
  };

  const updateMediaForAd = async (
    assetId: number,
    key: keyof AdConfigModel,
  ) => {
    const result =
      adConfig &&
      (await dispatch(
        AdConfigCRUDActions.update(botId, {
          id: adConfig.id,
          [key]: assetId,
        }),
      ));
    if (isArray(result)) {
      return;
    }
    setAdConfig(result);
    return result;
  };

  const updateMedia = async (
    assetId: number,
    url: string,
    key: keyof AdConfigModel,
  ) => {
    if (draft) {
      return updateMediaForAd(assetId, key);
    }
    setLocalUpdates(prevLocalUpdates => ({
      ...prevLocalUpdates,
      [key]: assetId,
    }));
    return key === 'assetId'
      ? { imageUrl: url, videoUrl: url }
      : { thumbUrl: url };
  };

  const onImageUpload = async (assetId: number, url: string) => {
    const result = await updateMedia(assetId, url, 'assetId');
    if (!result) {
      return;
    }
    setFormValues(prevFormValues => ({
      ...prevFormValues,
      imageAssetId: assetId,
      imageUrl: result.imageUrl,
    }));
  };

  const onVideoUpload = async (assetId: number, url: string) => {
    const result = await updateMedia(assetId, url, 'assetId');
    if (!result) {
      return;
    }
    setFormValues(prevFormValues => ({
      ...prevFormValues,
      videoAssetId: assetId,
      videoUrl: result.videoUrl,
    }));
  };
  const onThumbUpload = async (assetId: number, url: string) => {
    const result = await updateMedia(assetId, url, 'thumbId');
    if (!result) {
      return;
    }
    setFormValues(prevFormValues => ({
      ...prevFormValues,
      thumbAssetId: assetId,
      thumbUrl: result.thumbUrl,
    }));
  };

  const setTouchedFormsLevel = (level: number) => {
    setTouchedForms(Math.max(level, touchedForms));
  };

  const wrappedUpdate: <T extends (...args: any) => void>(
    level: number,
    callback: T,
  ) => (...params: Parameters<T>) => void =
    (level, callback) =>
    (...args) => {
      setTouchedFormsLevel(level);
      // @ts-ignore
      callback(...args);
    };

  const importStartBot = useCallback(
    wrappedUpdate(5, async (pageId: number) => {
      if (pageId === page?.id || !adConfig) {
        return;
      }
      if (adConfig.draft) {
        return handleRemoteUpdate({ pageId });
      }
      const ad = await dispatch(
        AdConfigCRUDActions.prepareReplacementPage(
          botId,
          Number(adConfig.id),
          pageId,
        ),
      );
      setAdConfig(ad);
    }),
    [adConfig, page],
  );

  const onVideoUploadCb = useCallback(wrappedUpdate(4, onVideoUpload), [
    botId,
    adConfig,
    draft,
  ]);
  const onImageUploadCb = useCallback(wrappedUpdate(4, onImageUpload), [
    botId,
    adConfig,
    draft,
  ]);
  const onThumbUploadCb = useCallback(wrappedUpdate(4, onThumbUpload), [
    botId,
    adConfig,
    draft,
  ]);

  const isTouchedStep = useCallback(
    (index: number) => {
      return touchedForms >= index + 1;
    },
    [touchedForms],
  );
  const isValid = useCallback(
    (index: number) => {
      if (index === 4) {
        return true;
      }
      return isValidStep(stepFields[index], validations);
    },
    [validations],
  );
  const isCollapsed = (index: number) => {
    if (!active || nextClicked < 0) {
      return true;
    }
    if (index === 0) {
      return false;
    }
    return nextClicked < index;
  };
  const configs = [
    {
      header: 'Select Ad Account',
      children: (
        <AdAccountStep
          onChange={wrappedUpdate(1, handleAccountChange)}
          formValues={formValues}
          adConfig={adConfig}
          validations={validations['adAccount']}
          accounts={accounts}
          usedInSlideout={usedInSlideout}
        />
      ),
    },
    {
      header: 'Set up Campaign',
      children: (
        <AdCampaignStep
          adConfig={adConfig}
          campaignName={formValues.campaignName}
          campaignBudgetType={formValues.campaignBudgetType}
          campaignBudget={formValues.campaignBudget}
          specialAdCategory={formValues.specialAdCategory}
          currency={currency}
          budgetOptimizationOn={budgetOptimizationOn}
          onChangeCampaignName={handleChangeWrapper('campaignName')}
          onBlurCampaignName={wrappedUpdate(2, () => {
            handleRemoteUpdate(
              {
                campaignName: formValues.campaignName,
              },
              {},
              'campaignName',
            );
          })}
          onChangeCampaignBudget={handleChangeWrapper('campaignBudget')}
          onBlurCampaignBudget={wrappedUpdate(2, () => {
            handleRemoteUpdate(
              budgetValue('campaignBudget'),
              {},
              'campaignBudget',
            );
          })}
          onChangeCampaignBudgetType={wrappedUpdate(
            2,
            (id: CampaignBudgetType) => {
              const scheduling =
                id === CampaignBudgetType.LIFETIME
                  ? {
                      adSetStartDate:
                        formValues.adSetStartDate.format(DATE_FORMAT),
                      adSetEndDate: formValues.adSetEndDate.format(DATE_FORMAT),
                    }
                  : {};
              const changes = {
                campaignBudgetType: id,
              };
              handleRemoteUpdate(
                { ...changes, ...scheduling },
                changes,
                'campaignBudget',
              );
            },
          )}
          onChangeSpecialCategory={wrappedUpdate(2, (id: SpecialAdCategory) => {
            const changes = {
              specialAdCategory: id,
            };
            handleRemoteUpdate(changes, changes, 'specialAdCategory');
          })}
          onToggleSpecialCategory={wrappedUpdate(2, e => {
            const formChanges = e.target.checked
              ? {
                  targeting: prepareSpecialTargeting(
                    formValues.targeting,
                    formValues.adAccount,
                  ),
                }
              : {
                  targeting: prepareNormalTargeting(
                    formValues.targeting,
                    formValues.adAccount,
                  ),
                };
            const changes = {
              specialAdCategory: e.target.checked
                ? formValues.specialAdCategory
                : null,
              ...formChanges,
            };
            handleRemoteUpdate(changes, formChanges, 'specialAdCategory');
          })}
          onBudgetOptimizationToggle={wrappedUpdate(
            2,
            onBudgetOptimizationToggle,
          )}
          campaignNameValidations={validations['campaignName']}
          campaignBudgetValidations={validations['campaignBudget']}
          usedInSlideout={usedInSlideout}
        />
      ),
    },
    {
      header: 'Set up Ad Set',
      children: (
        <AdSetStep
          adConfig={adConfig}
          currency={currency}
          specialAdCategoryChecked={specialAdCategoryChecked}
          customAudiencesMismatching={customAudiencesMismatching}
          onSelectCloneAdSet={wrappedUpdate(3, onSelectCloneAdSet)}
          onSelectNewAdSet={wrappedUpdate(3, onSelectNewAdSet)}
          onAdSetChange={wrappedUpdate(3, onAdSetChange)}
          showAdSetsSpinner={showAdSetsSpinner}
          budgetOptimizationOn={budgetOptimizationOn}
          adSetId={formValues.sourceAdSet?.id || ''}
          adSetName={formValues.adSetName}
          adSetBudgetType={formValues.adSetBudgetType}
          adSetBudget={formValues.adSetBudget}
          bidAmount={formValues.bidAmount}
          campaignBudgetType={formValues.campaignBudgetType}
          adSetValidations={validations['sourceAdSet']}
          adSetNameValidations={validations['adSetName']}
          adSetBudgetValidations={validations['adSetBudget']}
          bidAmountValidations={validations['bidAmount']}
          optimizationGoal={formValues.optimizationGoal}
          onChangeAdSetName={handleChangeWrapper('adSetName')}
          onBlurAdSetName={wrappedUpdate(3, () => {
            handleRemoteUpdate(
              { adSetName: formValues.adSetName },
              {},
              'adSetName',
            );
          })}
          onChangeOptimizationGoal={wrappedUpdate(3, (id: OptimizationGoal) => {
            const changes = { optimizationGoal: id };
            handleRemoteUpdate(changes, changes, 'optimizationGoal');
          })}
          onChangeAdSetBudget={handleChangeWrapper('adSetBudget')}
          onBlurAdSetBudget={wrappedUpdate(3, () => {
            handleRemoteUpdate(budgetValue('adSetBudget'), {}, 'adSetBudget');
          })}
          onChangeAdSetBudgetType={wrappedUpdate(3, (id: AdSetBudgetType) => {
            const scheduling =
              id === AdSetBudgetType.LIFETIME
                ? {
                    adSetStartDate:
                      formValues.adSetStartDate.format(DATE_FORMAT),
                    adSetEndDate: formValues.adSetEndDate.format(DATE_FORMAT),
                  }
                : {};
            const changes = {
              adSetBudgetType: id,
            };
            handleRemoteUpdate(
              { ...changes, ...scheduling },
              changes,
              'adSetBudget',
            );
          })}
          onBlurBidAmount={wrappedUpdate(3, () => {
            handleRemoteUpdate(budgetValue('bidAmount'), {}, 'bidAmount');
          })}
          onChangeBidAmount={handleChangeWrapper('bidAmount')}
          onChangeStartDate={wrappedUpdate(3, (date: Date) => {
            const startDate = noPastDate(date);
            const endNoLessThanStartDate = moment.max(
              formValues.adSetEndDate,
              startDate.clone().add(1, 'd'),
            );
            const changes = {
              adSetStartDate: startDate,
              adSetEndDate: endNoLessThanStartDate,
            };
            setFormValues({ ...formValues, ...changes });
          })}
          onChangeEndDate={wrappedUpdate(3, (date: Date) => {
            const endDate = noPastDate(date);
            const noLessThanStartDate = moment.max(
              formValues.adSetStartDate.clone().add(1, 'd'),
              endDate,
            );
            setFormValues({
              ...formValues,
              adSetEndDate: noLessThanStartDate,
            });
          })}
          onBlurStartDate={() => {
            handleRemoteUpdate({
              adSetStartDate: formValues.adSetStartDate.format(DATE_FORMAT),
              adSetEndDate: formValues.adSetEndDate.format(DATE_FORMAT),
            });
          }}
          onBlurEndDate={() => {
            handleRemoteUpdate({
              adSetEndDate: formValues.adSetEndDate.format(DATE_FORMAT),
            });
          }}
          onScheduleRanged={wrappedUpdate(3, onScheduleRanged)}
          onScheduleContinuously={wrappedUpdate(3, onScheduleContinuously)}
          adSetStartDate={formValues.adSetStartDate.toDate()}
          adSetEndDate={formValues.adSetEndDate.toDate()}
          targeting={formValues.targeting}
          customAudiences={formValues.adAccount?.customAudiences || []}
          onChangeTargeting={wrappedUpdate(3, (targeting: MMTargeting) => {
            if (!draft) {
              return;
            }
            handleRemoteUpdate({ targeting }, { targeting });
          })}
          scheduledContinuously={scheduledContinuously}
          adSets={adSets}
          usedInSlideout={usedInSlideout}
        />
      ),
    },
    {
      header: 'Create Ad',
      children: (
        <AdCreativeStep
          adConfig={adConfig}
          ads={ads}
          sourceAdId={formValues.sourceAd?.id || ''}
          creativeText={formValues.creativeText}
          creativeHeadline={formValues.creativeHeadline}
          creativeDescription={formValues.creativeDescription}
          showAdsSpinner={showAdsSpinner}
          isVideoMedia={isVideoMedia}
          usedInSlideout={usedInSlideout}
          videoUrl={formValues.videoUrl}
          imageUrl={formValues.imageUrl}
          thumbUrl={formValues.thumbUrl}
          imageAssetId={formValues.imageAssetId}
          thumbAssetId={formValues.thumbAssetId}
          callToAction={formValues.callToAction}
          onSelectCloneAd={wrappedUpdate(4, () => onSelectCloneAd())}
          onSelectNewAd={wrappedUpdate(4, onSelectNewAd)}
          onAdChange={wrappedUpdate(4, onAdChange)}
          onSelectImageMedia={wrappedUpdate(4, onSelectImageMedia)}
          onSelectVideoMedia={wrappedUpdate(4, onSelectVideoMedia)}
          onImageUpload={onImageUploadCb}
          onVideoUpload={onVideoUploadCb}
          onThumbUpload={onThumbUploadCb}
          onChangeCreativeText={handleUpdatedTextWrapper('creativeText')}
          onChangeCreativeHeadline={handleUpdatedTextWrapper(
            'creativeHeadline',
          )}
          onChangeCreativeDescription={handleUpdatedTextWrapper(
            'creativeDescription',
          )}
          onBlurCreativeText={wrappedUpdate(4, () => {
            handleRemoteUpdate(
              {
                creativeText: formValues.creativeText,
              },
              {},
              'creativeText',
            );
          })}
          onBlurCreativeHeadline={wrappedUpdate(4, () => {
            handleRemoteUpdate(
              {
                creativeHeadline: formValues.creativeHeadline,
              },
              {},
              'creativeHeadline',
            );
          })}
          onBlurCreativeDescription={wrappedUpdate(4, () => {
            handleRemoteUpdate(
              {
                creativeDescription: formValues.creativeDescription,
              },
              {},
              'creativeDescription',
            );
          })}
          onChangeCallToAction={wrappedUpdate(4, (id: CallToAction) => {
            const changes = {
              callToAction: id,
            };
            handleRemoteUpdate(changes, changes, 'callToAction');
          })}
          assetIdValidations={validations['assetId']}
          thumbIdValidations={validations['thumbId']}
          creativeTextValidations={validations['creativeText']}
          creativeHeadlineValidations={validations['creativeHeadline']}
          creativeDescriptionValidations={validations['creativeDescription']}
        />
      ),
    },
    {
      renderDropdownOptions:
        nextClicked >= 4
          ? () => {
              return (
                <>
                  {adConfig && (
                    <DialogueSettingsButton
                      pageId={adConfig.replacementPageId || adConfig.pageId}
                    />
                  )}
                  <StartBotAdsSelector onBotSelect={importStartBot} />
                </>
              );
            }
          : undefined,
      header: !usedInSlideout ? (
        <div className="ad-detail__create-dialogue-header">
          <span>
            Create Dialogue{' '}
            <a
              className="ad-detail__test-bot"
              href="#"
              onClick={e => {
                e.stopPropagation();
                testBot();
              }}
            >
              Test my bot
            </a>
          </span>
          <DialogueHeaderWarningAlert alertClassName="ad-detail--warning-alert" />
        </div>
      ) : (
        ' Create Dialogue'
      ),
      children: !usedInSlideout ? (
        <StartBotStep
          pageWidgets={pageWidgets}
          valid={valid}
          adConfig={adConfig}
          botId={botId}
        />
      ) : (
        <EmbeddableDialogueEditorWithPreview
          editPaneClassname="ad-detail__embeddable-dialogue-editor"
          previewClassName="ad-detail__embeddable-dialogue-editor-preview"
          dialogueId={
            adConfig?.replacementPageId ||
            adConfig?.pageId ||
            adConfigEntity?.pageId
          }
          isDisabled={adConfig?.publishingProcess}
          adConfig={adConfig}
          platforms={['facebook']}
          usedInSlideout={usedInSlideout}
          hideDialogueSelector
          forAds
        />
      ),
    },
  ];

  const [stepsStatus, setStepsStatus] = useFormSteps(
    configs,
    isCollapsed,
    nextClicked,
  );
  const onNext = (index: number, allowed?: boolean) => {
    setTouchedForms(Math.max(touchedForms, index + 1));
    if (allowed) {
      setNextClicked(index + 1);
      if (
        adConfig?.draft &&
        (adConfig.progressStep === null || adConfig.progressStep < index + 1)
      ) {
        setAdConfig({ ...adConfig, progressStep: index + 1 });
        dispatch(
          AdConfigCRUDActions.update(botId, {
            id: adConfig.id,
            progressStep: index + 1,
          }),
        );
      }
    }
  };
  const formSteps = formStepContainerBuilder({
    configs,
    options: {
      className: 'ad-detail__form-step',
      contentClassName: 'ad-detail__form-step-collapsible-content',
      indexOffset,
      isCollapsed,
      isTouched: isTouchedStep,
      isValid,
      nextAllowed: (index: number) => isValid(index) || nextClicked > index,
      onNext,
      stepsStatus,
      setStepsStatus,
    },
  });

  return (
    <div
      className={
        !usedInSlideout ? 'ad-detail-container' : 'ad-detail-container-slideout'
      }
    >
      <EditAdConfigHeader
        adConfig={adConfig}
        canPublish={valid && !adConfig?.publishingProcess}
        onSaveName={handleNameSave}
        errors={validations['name']}
        updates={localUpdates}
        updated={updated}
        isTemplateBot={Boolean(activeBot.template)}
        showOnlyActionButton={usedInSlideout}
        actionButtonClassName={
          usedInSlideout ? 'ad-detail__slideout-header-action-button' : ''
        }
        usedInSlideout={usedInSlideout}
        onActivateSuccess={onActivateSuccess}
      />
      <InactiveBotAlert />
      <November2020EuroAdsChangesAlert />
      {children?.({
        startOver: () => {
          onNext(-1, true);
          setStepsStatus([true, ...stepsStatus.slice(1).map(() => false)]);
        },
        currentAd: adConfig,
        currentStep: nextClicked,
      })}
      {!usedInSlideout && formSteps}
      {usedInSlideout &&
        configs.map((item, index) => {
          return (
            <CollapsibleContentContainer
              key={item.header.toString()}
              orderNumber={(index + 2).toString()}
              containerName={item.header}
              className={classNames(
                'ad-detail__slideout-step-container',
                'ad-detail__slideout-step-container-reduced-margin',
              )}
              stepperStylingPlatform="facebook"
              useCircleNumberStyle
            >
              {item.children}
            </CollapsibleContentContainer>
          );
        })}
    </div>
  );
};
